import type { OpenTokState } from './opentok';
import type { BroadcastingState } from './broadcasting';
import type { ChatReducerState } from './chat';
import type { ContentState } from './content';
import type { HomeState } from './home';
import type { InvitationsState } from './invitations';
import type { MediaState } from './media';
import type { ModalState } from './modal';
import type { NotesReducerState } from './notes';
import type { NotificationsState } from './notifications';
import type { SessionState } from './session';
import type { SettingsReducerState } from './settings';
import type { VideoState } from './video';
import type { ShotsState } from './shots';
import type { SegmentsState } from './segments';
import type { UserInterfaceState } from './user-interface';
import type { TeleprompterReducerState } from './teleprompters';
import type { GraphicsState } from './graphics';
import type { DebugState } from './debug';
import type { MeetingReducerState } from './meeting';
import type { CommToggleState } from './comm-toggle';
import type { GeneratedContentState } from './generate-content';
import type { IFeatureFlagsReducer } from './blp-feature-flags';
import type { TabsState } from './tabs';
import type { MasksState } from './masks';
import type { ContentChipsState } from './content-chips';
import type { ThemesState } from './themes';
import type { FontAssetsState } from './font-assets';
import type { LivekitState } from './livekit';
import type { DopState } from '../reducers/dop';

export enum EAsyncStatus {
	Incomplete = 'incomplete',
	Waiting = 'waiting',
	Error = 'error',
	Complete = 'complete'
}

export { ChatRoomStatus } from './common';

export enum HttpRequest {
	Get = 'GET',
	Head = 'HEAD',
	Post = 'POST',
	Put = 'PUT',
	Delete = 'DELETE',
	Connect = 'CONNECT',
	Options = 'OPTIONS',
	Trace = 'TRACE',
	Patch = 'PATCH',
}

export interface Bitrate {
	[key: string]: number;
}

export interface Action {
	type: string;
	payload: any;
	remote?: boolean; // this action came from a remote user (usually via src/socket-listeners/sync)
	id?: number;
	socket?: boolean; // if the action was dispatched from a socket listener
}

export interface ActionGeneric<T> extends Action {
	payload: T;
}

export interface AsyncAction {
	type: string;
	promise: Promise<any>
	socket?: boolean; // if the action was dispatched from a socket listener
}

export interface BackgroundImage {
	url: string;
	coverage: 'fit' | 'fill' | 'stretch' | 'none' | 'tile',
	scale: number;
}

export interface GreenroomState {
	OpenTokReducer: OpenTokState;
	BroadcastReducer: BroadcastingState;
	ChatReducer: ChatReducerState;
	ContentReducer: ContentState;
	HomeReducer: HomeState;
	InvitationsReducer: InvitationsState;
	MediaReducer: MediaState;
	ModalReducer: ModalState;
	NotesReducer: NotesReducerState;
	NotificationsReducer: NotificationsState;
	SessionReducer: SessionState;
	SettingsReducer: SettingsReducerState;
	VideoReducer: VideoState;
	ShotsReducer: ShotsState;
	SegmentsReducer: SegmentsState;
	UserInterfaceReducer: UserInterfaceState;
	TeleprompterReducer: TeleprompterReducerState;
	GraphicsReducer: GraphicsState;
	DebugReducer: DebugState;
	MeetingReducer: MeetingReducerState;
	CommToggleReducer: CommToggleState;
	GeneratedContentReducer: GeneratedContentState;
	FeatureFlagsReducer: IFeatureFlagsReducer;
	TabsReducer: TabsState;
	MasksReducer: MasksState;
	ContentChipsReducer: ContentChipsState
	ThemesReducer: ThemesState
	FontAssetsReducer: FontAssetsState
	LivekitReducer: LivekitState
	DopReducer: DopState
}

export interface VideoRect {
	width: string;
	height: string;
	top: string;
	left: string;
}

export interface IDateObjectProps {
	weekday: string;
	day: string;
	month: string;
	hour: string;
	minute: string;
	hourType: string;
}

export interface GraphicDimensions {
	xScale: number;
	yScale: number;
}

export interface MediaDimensions {
	width?: number;
	height?: number;
}

// This can be used like keyof for values
export type valueof<T> = T[keyof T];

export type RecursivePartial<T> = {
    [P in keyof T]?: RecursivePartial<T[P]>;
};

export type ApiError = {
	error: string;
}

export type { StringIndexObject } from './common';

// Can use instead of `{ [key: number]: Type }`
export type NumberIndexObject<T> = {
    [key: number]: T
}

export interface CssPosition {
	top?: number;
	right?: number;
	bottom?: number;
	left?: number;
}

export type LogLevel = 'info' | 'warn' | 'error' | 'debug';

export enum DeviceTypes {
	Camera = 'Camera',
	Microphone = 'Microphone',
	Speakers = 'Speakers',
}

export { ChannelAuthMethod } from './common';
